// frontend/packages/business-console/src/pages/Unauthorized.jsx

import React, { useMemo } from "react";
import { Box, Button, Container, Paper, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "support@upryzze.com";

const Unauthorized = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const details = useMemo(() => {
    const path = location?.pathname || "";
    const state = location?.state || {};
    const requestId = state?.requestId || "";
    return { path, requestId };
  }, [location?.pathname, location?.state]);

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Oops — we can’t open that page
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
            This usually happens if your account doesn’t have access to this area, or if your
            sign-in session needs to be refreshed.
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              What you can do next
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              1) Try signing out and signing back in.
              <br />
              2) If the problem continues, contact support at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "inherit" }}>
                {SUPPORT_EMAIL}
              </a>
              .
            </Typography>
          </Box>

          {!process.env.NODE_ENV || process.env.NODE_ENV !== "production" ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ opacity: 0.65, display: "block" }}>
                Path: {details.path || "(unknown)"}
              </Typography>
              {details.requestId ? (
                <Typography variant="caption" sx={{ opacity: 0.65, display: "block" }}>
                  Request ID: {details.requestId}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={() => navigate("/login", { replace: true })}
              sx={{ fontWeight: 800 }}
            >
              Back to Login
            </Button>

            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  await logout();
                } catch {
                  navigate("/login", { replace: true });
                }
              }}
              sx={{ fontWeight: 800 }}
            >
              Sign out
            </Button>

            <Button
              variant="text"
              onClick={() => navigate(-1)}
              sx={{ fontWeight: 700 }}
            >
              Go Back
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default React.memo(Unauthorized);